import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { Zap, Star, Crown, Check, ArrowRight, MessageCircle, Loader2, CreditCard, QrCode } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useApi } from '../../services/api'
import type { Plan } from '../../services/api'
import LoginModal from './LoginModal'
import RegisterModal from './RegisterModal'
import logoTander from '../../assets/WhatsApp_Image_2025-08-29_at_16.10.10-removebg-preview.png'

const PricingPlans = () => {
  const navigate = useNavigate()
  const [apiPlans, setApiPlans] = useState<Plan[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showLoginModal, setShowLoginModal] = useState(false)
  const [showRegisterModal, setShowRegisterModal] = useState(false)
  const [showPaymentModal, setShowPaymentModal] = useState(false)
  const [selectedPlan, setSelectedPlan] = useState<Plan | null>(null)
  const [processingPayment, setProcessingPayment] = useState(false)
  const { apiService, isAuthenticated } = useApi()

  // Planos estáticos com layout fixo
  const staticPlans = [
    {
      id: 1,
      name: 'Personal',
      description: 'Perfeito para usuários individuais e pequenos projetos.',
      price: 1000, // Valor padrão em centavos
      currency: 'BRL',
      billing_interval: 'monthly',
      features: [
        'Até 100 documentos/mês',
        'Análise básica com IA',
        'Exportação em Word/Excel',
        'Suporte por email',
        '1 usuário'
      ]
    },
    {
      id: 2,
      name: 'Premium',
      description: 'Ideal para equipes e projetos profissionais.',
      price: 1000, // Valor padrão em centavos
      currency: 'BRL',
      billing_interval: 'monthly',
      features: [
        'Até 1.000 documentos/mês',
        'Análise avançada com múltiplas IAs',
        'Templates personalizáveis',
        'Suporte prioritário',
        'Até 5 usuários',
        'API access',
        'Webhooks'
      ]
    },
    {
      id: 3,
      name: 'Business',
      description: 'Soluções enterprise com recursos personalizados.',
      price: 0,
      currency: 'BRL',
      billing_interval: 'monthly',
      features: [
        'Documentos ilimitados',
        'IA dedicada e personalizada',
        'Integrações customizadas',
        'Suporte 24/7 dedicado',
        'Usuários ilimitados',
        'SLA garantido',
        'On-premise disponível'
      ]
    }
  ]

  // Função para combinar planos estáticos com preços da API
  const getPlansWithApiPrices = () => {
    return staticPlans.map(staticPlan => {
      const apiPlan = apiPlans.find(api => api.name === staticPlan.name || api.id === staticPlan.id)
      return {
        ...staticPlan,
        price: apiPlan ? apiPlan.price : staticPlan.price
      }
    })
  }

  // Carregar preços da API
  useEffect(() => {
    const loadPlans = async () => {
      try {
        setIsLoading(true)
        const response = await apiService.getPlans()
        console.log('📦 [PricingPlans] Planos recebidos da API:', response)
        setApiPlans(response.plans || [])
      } catch (error) {
        console.error('Erro ao carregar planos da API:', error)
        setError(error instanceof Error ? error.message : 'Erro ao carregar planos')
        // Manter planos estáticos em caso de erro
        setApiPlans([])
      } finally {
        setIsLoading(false)
      }
    }

    loadPlans()
  }, [apiService])

  // Planos finais (estáticos + preços da API)
  const plans = getPlansWithApiPrices()

  const handlePlanSelect = (plan: Plan) => {
    if (plan.name === 'Business') {
      // Redirecionar para WhatsApp para plano Business
      window.open('https://wa.me/5511999999999?text=Olá! Gostaria de saber mais sobre o plano Business do TANDER', '_blank')
      return
    }

    // Se não estiver autenticado, mostrar modal de cadastro
    if (!isAuthenticated) {
      setSelectedPlan(plan)
      setShowRegisterModal(true)
      return
    }

    // Se estiver autenticado, mostrar modal de pagamento
    setSelectedPlan(plan)
    setShowPaymentModal(true)
  }

  const handleRegisterSuccess = () => {
    setShowRegisterModal(false)
    // Após cadastro bem-sucedido, mostrar login
    setShowLoginModal(true)
  }

  const handleLoginSuccess = () => {
    setShowLoginModal(false)
    // Após login bem-sucedido, mostrar modal de pagamento
    if (selectedPlan) {
      setShowPaymentModal(true)
    }
  }

  const handleSwitchToLogin = () => {
    setShowRegisterModal(false)
    setShowLoginModal(true)
  }

  const handleSwitchToRegister = () => {
    setShowLoginModal(false)
    setShowRegisterModal(true)
  }

  const handlePaymentMethod = async (method: 'credit_card' | 'pix') => {
    if (!selectedPlan) return

    try {
      setProcessingPayment(true)
      
      if (method === 'pix') {
        // Criar assinatura PIX via API
        const response = await apiService.createSubscription({
          plan_type: selectedPlan.id,
          payment_method: 'pix'
        })
        
        if (response.success) {
          // Redirecionar para página PIX com dados reais
          const params = new URLSearchParams({
            plan: selectedPlan.id.toString(),
            plan_name: selectedPlan.name,
            price: (selectedPlan.price / 100).toFixed(2),
            currency: selectedPlan.currency,
            qr_code: (response as any).qr_code || '',
            payment_url: (response as any).payment_url || '',
            pix_key: (response as any).pix_key || 'tander@email.com',
            transaction_id: (response as any).payment_id || (response as any).subscription_id || ''
          })
          
          // Usar navigate para redirecionamento correto
          navigate(`/pagamento/pix?${params.toString()}`)
        } else {
          throw new Error(response.message || 'Erro ao criar PIX')
        }
      } else {
        // Para cartão, redirecionar para página de cartão
        const params = new URLSearchParams({
          plan: selectedPlan.id.toString(),
          plan_name: selectedPlan.name,
          price: (selectedPlan.price / 100).toFixed(2),
          currency: selectedPlan.currency
        })
        
        // Usar navigate para redirecionamento correto
        navigate(`/pagamento/cartao?${params.toString()}`)
      }
      
    } catch (error) {
      console.error('Erro ao processar pagamento:', error)
      alert(`Erro: ${error instanceof Error ? error.message : 'Erro desconhecido'}`)
    } finally {
      setProcessingPayment(false)
      setShowPaymentModal(false)
    }
  }

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  }

  const cardVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0 }
  }

  if (isLoading) {
    return (
      <section id="planos" className="section-padding bg-gray-50">
        <div className="container-custom">
          <div className="text-center">
            <Loader2 className="w-12 h-12 text-primary-500 animate-spin mx-auto mb-4" />
            <p className="text-gray-600">Carregando planos...</p>
          </div>
        </div>
      </section>
    )
  }

  return (
    <>
      <section id="planos" className="section-padding bg-gray-50">
        <div className="container-custom">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Planos Inteligentes
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Escolha o plano ideal para suas necessidades.
            </p>
          </motion.div>

          {/* Error Message */}
          {error && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-red-50 border border-red-200 rounded-xl p-4 mb-8 text-center"
            >
              <p className="text-red-600">{error}</p>
            </motion.div>
          )}

          {/* Plans Grid */}
          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid lg:grid-cols-3 gap-8 mb-16"
          >
            {plans.map((plan) => (
              <motion.div
                key={plan.id}
                variants={cardVariants}
                whileHover={{ y: -10 }}
                className={`relative ${plan.name === 'Premium' ? 'lg:scale-105' : ''}`}
              >
                {/* Popular Badge */}
                {plan.name === 'Premium' && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 z-10">
                    <div className="bg-accent-500 text-white px-4 py-2 rounded-full text-sm font-semibold shadow-lg">
                      Mais Popular
                    </div>
                  </div>
                )}

                <div className={`card p-8 h-full ${plan.name === 'Premium' ? 'ring-2 ring-accent-500' : ''}`}>
                  {/* Plan Header */}
                  <div className="text-center mb-8">
                    <div className={`inline-flex items-center justify-center w-16 h-16 rounded-2xl ${
                      plan.name === 'Personal' ? 'bg-primary-100 text-primary-500' :
                      plan.name === 'Premium' ? 'bg-accent-100 text-accent-500' :
                      'bg-gray-100 text-gray-500'
                    } mb-4`}>
                      {plan.name === 'Personal' && <Zap className="w-8 h-8" />}
                      {plan.name === 'Premium' && <Star className="w-8 h-8" />}
                      {plan.name === 'Business' && <Crown className="w-8 h-8" />}
                    </div>
                    
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">
                      {plan.name}
                    </h3>
                    
                    <div className="mb-4">
                      {plan.price === 0 ? (
                        <span className="text-2xl font-bold text-gray-900">Personalizado</span>
                      ) : (
                        <>
                          <span className="text-4xl font-bold text-gray-900">R$ {(plan.price / 100).toFixed(2)}</span>
                          <span className="text-gray-600">/mês</span>
                        </>
                      )}
                    </div>
                    
                    <p className="text-gray-600">
                      {plan.description}
                    </p>
                  </div>

                  {/* Features */}
                  <div className="mb-8">
                    <ul className="space-y-3">
                      {plan.features && plan.features.length > 0 ? (
                        plan.features.map((feature, featureIndex) => (
                          <li key={featureIndex} className="flex items-center space-x-3">
                            <Check className="w-5 h-5 text-green-500 flex-shrink-0" />
                            <span className="text-gray-700">{feature}</span>
                          </li>
                        ))
                      ) : (
                        // Fallback features baseadas no nome do plano
                        (() => {
                          const fallbackFeatures = {
                            'Personal': [
                              'Até 100 documentos/mês',
                              'Análise básica com IA',
                              'Exportação em Word/Excel',
                              'Suporte por email',
                              '1 usuário'
                            ],
                            'Premium': [
                              'Até 1.000 documentos/mês',
                              'Análise avançada com múltiplas IAs',
                              'Templates personalizáveis',
                              'Suporte prioritário',
                              'Até 5 usuários',
                              'API access',
                              'Webhooks'
                            ],
                            'Business': [
                              'Documentos ilimitados',
                              'IA dedicada e personalizada',
                              'Integrações customizadas',
                              'Suporte 24/7 dedicado',
                              'Usuários ilimitados',
                              'SLA garantido',
                              'On-premise disponível'
                            ]
                          }
                          
                          const features = fallbackFeatures[plan.name as keyof typeof fallbackFeatures] || [
                            'Recursos básicos incluídos',
                            'Suporte técnico',
                            'Atualizações regulares'
                          ]
                          
                          return features.map((feature, featureIndex) => (
                            <li key={featureIndex} className="flex items-center space-x-3">
                              <Check className="w-5 h-5 text-green-500 flex-shrink-0" />
                              <span className="text-gray-700">{feature}</span>
                            </li>
                          ))
                        })()
                      )}
                    </ul>
                  </div>

                  {/* CTA Button */}
                  <div className="mt-auto">
                    {plan.name === 'Business' ? (
                      <motion.a
                        href="https://wa.me/5511999999999?text=Olá! Gostaria de saber mais sobre o plano Business do TANDER"
                        target="_blank"
                        rel="noopener noreferrer"
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        className="btn-secondary w-full flex items-center justify-center space-x-2"
                      >
                        <MessageCircle className="w-5 h-5" />
                        <span>Falar com vendas</span>
                      </motion.a>
                    ) : (
                      <motion.button
                        onClick={() => handlePlanSelect(plan)}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        className={`btn-${plan.name === 'Premium' ? 'secondary' : 'primary'} w-full flex items-center justify-center space-x-2`}
                      >
                        <span>
                          {plan.name === 'Personal' ? 'Escolher Personal' : 'Escolher Premium'}
                        </span>
                        <ArrowRight className="w-5 h-5" />
                      </motion.button>
                    )}
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>

          {/* Additional Info */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="text-center"
          >
            <div className="bg-white rounded-2xl p-8 shadow-lg">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                Todos os planos incluem
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                <div className="text-center">
                  <div className="w-12 h-12 bg-primary-100 rounded-xl flex items-center justify-center mx-auto mb-3">
                    <Zap className="w-6 h-6 text-primary-500" />
                  </div>
                  <div className="text-sm font-medium text-gray-900">Processamento rápido</div>
                </div>
                <div className="text-center">
                  <div className="w-12 h-12 bg-accent-100 rounded-xl flex items-center justify-center mx-auto mb-3">
                    <Star className="w-6 h-6 text-accent-500" />
                  </div>
                  <div className="text-sm font-medium text-gray-900">IA de qualidade</div>
                </div>
                <div className="text-center">
                  <div className="w-12 h-12 bg-primary-100 rounded-xl flex items-center justify-center mx-auto mb-3">
                    <Check className="w-6 h-6 text-primary-500" />
                  </div>
                  <div className="text-sm font-medium text-gray-900">Suporte técnico</div>
                </div>
                <div className="text-center">
                  <div className="w-12 h-12 bg-accent-100 rounded-xl flex items-center justify-center mx-auto mb-3">
                    <MessageCircle className="w-6 h-6 text-accent-500" />
                  </div>
                  <div className="text-sm font-medium text-gray-900">Atualizações</div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Register Modal */}
      <RegisterModal
        isOpen={showRegisterModal}
        onClose={() => setShowRegisterModal(false)}
        onSuccess={handleRegisterSuccess}
        onSwitchToLogin={handleSwitchToLogin}
      />

      {/* Login Modal */}
      <LoginModal
        isOpen={showLoginModal}
        onClose={() => setShowLoginModal(false)}
        onSuccess={handleLoginSuccess}
        onSwitchToRegister={handleSwitchToRegister}
      />

      {/* Payment Method Modal */}
      {showPaymentModal && selectedPlan && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0, y: 20 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl p-8"
          >
            {/* Header */}
            <div className="text-center mb-8">
              <div className="flex justify-center mb-4">
                <img 
                  src={logoTander} 
                  alt="TANDER SMART" 
                  className="h-12 w-auto"
                />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Escolha o Método de Pagamento</h2>
              <p className="text-gray-600">
                Para o plano <span className="font-semibold text-primary-600">{selectedPlan.name}</span>
              </p>
            </div>

            {/* Payment Methods */}
            <div className="space-y-4 mb-8">
              <motion.button
                onClick={() => handlePaymentMethod('credit_card')}
                disabled={processingPayment}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="w-full p-4 border-2 border-gray-200 rounded-xl hover:border-primary-500 hover:bg-primary-50 transition-all duration-200 flex items-center justify-center space-x-3 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <CreditCard className="w-6 h-6 text-primary-500" />
                <span className="font-semibold text-gray-900">
                  {processingPayment ? 'Processando...' : 'Cartão de Crédito'}
                </span>
              </motion.button>

              <motion.button
                onClick={() => handlePaymentMethod('pix')}
                disabled={processingPayment}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="w-full p-4 border-2 border-gray-200 rounded-xl hover:border-accent-500 hover:bg-accent-50 transition-all duration-200 flex items-center justify-center space-x-3 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <QrCode className="w-6 h-6 text-accent-500" />
                <span className="font-semibold text-gray-900">
                  {processingPayment ? 'Processando...' : 'PIX'}
                </span>
              </motion.button>
            </div>

            {/* Close Button */}
            <motion.button
              onClick={() => setShowPaymentModal(false)}
              disabled={processingPayment}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="w-full p-3 border border-gray-300 rounded-xl text-gray-700 hover:bg-gray-50 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancelar
            </motion.button>
          </motion.div>
        </motion.div>
      )}
    </>
  )
}

export default PricingPlans
